'use client';

import React from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import { Iconify } from 'src/components/iconify';
import StyledChipNonFade from 'src/components/styled-chip/styled-chip-non-fade';
import { IFärgKombo } from 'src/theme/NyttFärgtema';

// ----------------------------------------------------------------------

export interface DomainCardMetric {
    label: string;
    value: string | number;
}

export interface DomainCardStatus {
    label: string;
    color: string;
}

export interface DomainCardProps {
    title: string;
    icon: string;
    theme: IFärgKombo;
    href?: string;
    primaryMetric?: DomainCardMetric;
    secondaryMetric?: DomainCardMetric;
    statusChip?: DomainCardStatus;
    children?: React.ReactNode;
    openLabel?: string;
    /** Subtitle rendered when no primaryMetric is provided. */
    emptyMessage?: string;
}

export default function DomainCard({
    title,
    icon,
    theme: färgKombo,
    href,
    primaryMetric,
    secondaryMetric,
    statusChip,
    children,
    openLabel = 'Öppna',
    emptyMessage,
}: DomainCardProps) {
    const primary = färgKombo.primary.hexkod;
    const secondary = färgKombo.secondary.hexkod;

    return (
        <Paper
            elevation={0}
            sx={(theme) => ({
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 3,
                border: `1px solid ${alpha(primary, 0.12)}`,
                bgcolor: 'background.paper',
                transition: theme.transitions.create(['transform', 'box-shadow', 'border-color'], {
                    duration: theme.transitions.duration.shorter,
                }),
                '&:hover': {
                    transform: 'translateY(-3px)',
                    borderColor: alpha(primary, 0.4),
                    boxShadow: `0 12px 32px -16px ${alpha(primary, 0.45)}`,
                },
            })}
        >
            {/* Gradient header band */}
            <Box
                sx={{
                    position: 'relative',
                    px: 2.5,
                    pt: 2.5,
                    pb: 2,
                    background: `linear-gradient(135deg, ${alpha(primary, 0.16)} 0%, ${alpha(secondary, 0.12)} 100%)`,
                    borderBottom: `1px solid ${alpha(primary, 0.08)}`,
                }}
            >
                {/* Decorative blur circle */}
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        top: -30,
                        right: -30,
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        background: `radial-gradient(circle at center, ${alpha(secondary, 0.35)} 0%, transparent 70%)`,
                        pointerEvents: 'none',
                    }}
                />

                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ position: 'relative' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
                                color: 'common.white',
                                boxShadow: `0 6px 14px -6px ${alpha(primary, 0.6)}`,
                            }}
                        >
                            <Iconify icon={icon} width={24} />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {title}
                        </Typography>
                    </Stack>

                    {statusChip && (
                        <StyledChipNonFade label={statusChip.label} color={statusChip.color} />
                    )}
                </Stack>
            </Box>

            {/* Body */}
            <Box sx={{ px: 2.5, py: 2.25, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {primaryMetric ? (
                    <Stack direction="row" alignItems="baseline" spacing={2} sx={{ flexWrap: 'wrap' }}>
                        <Box>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 800,
                                    lineHeight: 1.1,
                                    color: primary,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                {primaryMetric.value}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                {primaryMetric.label}
                            </Typography>
                        </Box>

                        {secondaryMetric && (
                            <Box sx={{ pl: 2, borderLeft: (theme) => `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {secondaryMetric.value}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {secondaryMetric.label}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                ) : emptyMessage ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {emptyMessage}
                    </Typography>
                ) : null}

                {children && <Box sx={{ mt: primaryMetric || emptyMessage ? 0.5 : 0 }}>{children}</Box>}
            </Box>

            {/* Footer link */}
            {href && (
                <Box
                    sx={{
                        px: 2.5,
                        py: 1.5,
                        borderTop: (theme) => `1px dashed ${theme.palette.divider}`,
                        bgcolor: alpha(primary, 0.03),
                    }}
                >
                    <Link
                        href={href}
                        underline="none"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: '0.8125rem',
                            fontWeight: 700,
                            color: primary,
                            '&:hover': { color: secondary },
                            '&:hover .domain-card-arrow': { transform: 'translateX(4px)' },
                        }}
                    >
                        {openLabel}
                        <Iconify
                            icon="eva:arrow-ios-forward-fill"
                            width={16}
                            className="domain-card-arrow"
                            sx={(theme) => ({
                                transition: theme.transitions.create('transform', {
                                    duration: theme.transitions.duration.shorter,
                                }),
                            })}
                        />
                    </Link>
                </Box>
            )}
        </Paper>
    );
}
